import React, { useState } from 'react';
import { connect } from 'react-redux';
import { setEventCache, flagEventForExport, setSelectedVideoPlaying, setSelectedVideoPlayingUrl, showAlertModal } from '../../actions';
import { TiWarning } from 'react-icons/ti';
import { FaDotCircle, FaDownload, FaCar, FaMale, FaPlay } from 'react-icons/fa';
import { formatDisplayTS } from '../common/Utilities';
import loading from '../../downloading.gif';
import no_image from '../../no_image.png';
import axios from 'axios';

const VideoCard = props => {
    // Benbrook devices are connected in staging server, while the rest of the devices are connected in production server
    // when testing in local, connect to staging to be able to connect to a test device
    const API_URL = process.env.NODE_ENV === 'development' ? process.env.REACT_APP_VIDEO_BACKEND : process.env.REACT_APP_BACKEND

    const sDir = props.projects.find(p => p.bID === props.devices.find(d => d.sDeviceID === props.event.sDeviceID).bCustomerProjectID).sDir
    const eventUrl = `${API_URL}uploads/${sDir}${props.event.sDeviceID}/cam${props.event.bCamera}/video/${props.event.sTimeStamp}`
    const videoUrl = `${eventUrl}.mp4?device=${props.event.sDeviceID}`

    const [hovering, setHovering] = useState(false)
    const [polling, setPolling] = useState(false)
    const [progress, setProgress] = useState(props.event.dCachedProgress)
    const [pollingError, setPollingError] = useState(false)
    const [pollingErrorText, setPollingErrorText] = useState('')
    const [downloading, setDownloading] = useState(false)
    const [downloaded, setDownloaded] = useState(false)
    const [downloadBlob, setDownloadBlob] = useState('')
    const [downloadFilename, setDownloadFilename] = useState('')

    const handleOnClick = async () => {
      setPollingError(false)
      setPollingErrorText('')
      setProgress(0)

      if (props.event.fCached) {
        // we are cached and can just play it man!
        props.setSelectedVideoPlayingUrl(videoUrl)
        props.setSelectedVideoPlaying(props.event)
        return
      }

      // check if device is offline before attempting to get the data from the device
      if (canGetVideo()) {
        // request the video event from the device if its online
        requestVideoEvent()
      } else {
        setPollingError(true)
        setPollingErrorText('Device offline')
      }
    }

    const canGetVideo = () => {
      const device = props.devices.find(device => device.sDeviceID === props.event.sDeviceID)
      const online = (device && device.fOnline === 1) ? true : false
      return online
    }

    const requestVideoEvent = async () => {
      setPolling(true)

      // this initiates download of the video event and updates the JSEvent.dCacheProgress in the background
      await axios({
        method: 'get',
        url: `${API_URL}ve/videoevent?device=${props.event.sDeviceID}&cam=${props.event.bCamera}&ts=${props.event.sTimeStamp}`,
        headers: {
          'Authorization': 'Bearer ' + props.token
        }
      }).then((response) => {
        if (response.data && response.data.success) {
          pollEventDownloadProgress()
        } else {
          setPolling(false)
          setPollingError(true)
          setPollingErrorText('Device unreachable')
        }
        return 'test'
      }).catch( error => {
        setPolling(false)
        setPollingError(true)
        setPollingErrorText('Device unreachable')
      })
    }

    const pollEventDownloadProgress = async () => {
      // checks for the download progress
      await  axios({
        method: 'get',
        url: `${API_URL}ve/videoeventdownloadstatus?device=${props.event.sDeviceID}&id=${props.event.bID}`,
        headers: {
          'Authorization': 'Bearer ' + props.token
        }
      }).then((response) => {
        if (!response.data || !response.data.result) {
          throw new Error('bad response from get playback events.')
        }

        const result = response.data.result.dCacheProgress
        setProgress(result)

        if (result === 100 ) {
          // just put indication that event is now cached and ready to be played
          // we will not auto play for cases when user wants to click multiple cards and have it ready to be played without having to wait every single click
          props.setEventCache(props.events, props.filteredEvents, props.event.bID)
          setPolling(false)
        } else {
          // check if device is offline before attempting to get additional data from the device
          if (canGetVideo()) {
            // recursively call our polling function again
            setTimeout(() => pollEventDownloadProgress(), 1000)
          } else {
            setPolling(false)
            setPollingError(true)
            setPollingErrorText('Device offline')
          };
        }
      }).catch(error => {
        setPolling(false)
        setPollingError(true)
        setPollingErrorText('Download error')
      })
    }

    const handleExport = (e) => {
      const checked = e.target.checked ? 1 : 0
      props.flagEventForExport(props.token, props.event.sDeviceID, props.event.bID, checked, props.events, props.filteredEvents)
    }

    const handleDownload = async () => {
      setDownloading(true)

      // formulate the download filename
      const ts = props.event.convertedTS
      const date = `${ts.substring(5, 7)}-${ts.substring(8, 10)}-${ts.substring(0, 4)}`
      const time = `${ts.substring(11, 13)}${ts.substring(14, 16)}${ts.substring(17, 19)}`
      const filename = `cam${props.event.bCamera}_${date}_${time}.mp4`
      setDownloadFilename(filename)

      await axios({
        url: videoUrl,
        responseType: 'blob',
      }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        setDownloadBlob(url)

        // create a temporary download link, trigger link click and delete the element afterwards
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', filename)
        document.body.appendChild(link)
        link.click()
        link.parentNode.removeChild(link)
        setDownloaded(true)
      }).catch((error) => {
        console.error('Error downloading the video:', error)
      }).finally(() => {
        setDownloading(false)
      })
    }

    return (
      <div style={styles.container}>
        {/* thumbnail */}
        <div data-cardid={props.event.bID} data-ts={props.event.convertedTS} onClick={handleOnClick} onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}
          style={{ ...styles.thumbnail, border: hovering ? '4px solid dodgerblue' : (props.selectedVideoPlaying && props.selectedVideoPlaying.bID === props.event.bID) ? '4px solid lime'
            : (props.selectedEvent && props.selectedEvent.bID === props.event.bID) ? '4px solid dodgerblue' : '4px solid transparent' }}>

          <img onError={e => e.target.src = no_image} alt='' height={'100%'} width={'100%'} style={styles.image} src={`${eventUrl}.jpg?device=${props.event.sDeviceID}`} />

          {/* top bar  */}
          <div style={{...styles.topBar, backgroundColor: (props.event.sTags.split(',').includes('vehicle') && props.event.sTags.split(',').includes('person')) ? 'blue'
              : props.event.sTags.split(',').includes('vehicle') ? 'dodgerblue'
                : props.event.sTags.split(',').includes('person') ? 'lightseagreen' : 'rgba(0, 0, 0, 0.6)'}}>
            {(props.devices.find(device => device.sDeviceID === props.event.sDeviceID) && props.devices.find(device => device.sDeviceID === props.event.sDeviceID).fOnline !== 1) &&
              <div style={styles.topBarText}>device offline</div>
            }

            {pollingError
              ? <div style={styles.pollingErrorContainer}>
                  <TiWarning />
                  <div style={styles.topBarText}>{pollingErrorText}</div>
                </div>
              : polling
                ? <img alt='' style={styles.downloadGif} src={loading} height={12} width={12} />
                : props.event.fCached === 1
                  ? <FaDotCircle style={styles.cachedIcon} />
                  : props.event.fCached !== 1 && <div style={styles.topBarText}>Click to Load Video</div>
            }

            {/* download progress bar  */}
            {polling && props.event.fCached === 0 &&
              <div style={styles.outerProgressBar}>
                <div style={{ ...styles.innerProgressBar, width: `${progress + 1}%` }}></div>
              </div>
            }

            <div style={styles.iconContainer}>
              {props.event.sTags && props.event.sTags.split(',').includes('vehicle') && <FaCar />}
              {props.event.sTags && props.event.sTags.split(',').includes('person') && <FaMale />}
            </div>
          </div>

          {/* middle overlay */}
          {props.event.fCached === 1 && <div style={{...styles.playIcon, backgroundColor: hovering ? 'black' : 'rgba(0,0,0,0.6)'}} ><FaPlay /></div>}

          {/* bottom bar  */}
          <div style={ styles.bottomBar}>
            <div style={ styles.timestampStyle }>{formatDisplayTS(props.event.convertedTS, true)}</div>
            <div style={ styles.durationStyle }>{`${props.event.bDuration}s`}</div>
          </div>
        </div>

        {/* actions */}
        {props.event.fCached === 1 &&
          <div style={styles.actions}>
            <input type="checkbox" checked={props.event.fFlagged === 1 ? true : false} defaultValue={props.event.fFlagged} onChange={handleExport} style={styles.checkbox} title="Export" />
            <div>
              {downloading
                ? <img alt='' style={styles.downloading} src={loading} title="Preparing download file" />
                : downloaded
                  ? <a href={downloadBlob} download={downloadFilename} title="Downloaded" style={{...styles.download, color: 'limegreen'}}><FaDownload /></a>
                  : <FaDownload onClick={handleDownload} title="Download" style={styles.download} />
              }
            </div>
          </div>
        }
      </div>
    )
}

const mapStateToProps = state => {
  const { token } = state.auth;
  const { events, filteredEvents, selectedEvent, selectedVideoPlaying } = state.playback;
  const { dir } = state.config;
  const { projects, devices } = state.data;

  return {
    token,
    events,
    filteredEvents,
    selectedEvent,
    selectedVideoPlaying,
    dir,
    projects,
    devices,
  }
}

export default connect(mapStateToProps, { setEventCache, flagEventForExport, setSelectedVideoPlaying, setSelectedVideoPlayingUrl, showAlertModal })(VideoCard);

const styles = {
  container: {
    display: 'flex',
    gap: '0.5vw',
  },
  thumbnail: {
    position: 'relative',
    height: '8vw',
    width: '10vw',
    minHeight: '8vw',
    maxHeight: 120,
    maxWidth: 200,
    backgroundColor: 'rgba(0,0,0,0.1)',
    borderBottom: '4px solid lightgreen',
    borderRadius: 8,
    cursor: 'pointer',
  },
  image: {
    borderRadius: 6,
  },
  topBar: {
    width: '100%',
    minHeight: 'calc(1vmin + 4px)',
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    padding: '2px 5px',
    position: 'absolute',
    top: 0,
    borderTopRightRadius: 5,
    borderTopLeftRadius: 5,
  },
  topBarText: {
    fontSize: '1vmin',
  },
  pollingErrorContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 2
  },
  downloadGif: {
    marginTop: .5,
    marginLeft: -4
  },
  cachedIcon: {
    color: 'lime',
    marginTop: 2,
    marginLeft: -2
  },
  outerProgressBar: {
    width: '40%',
    height: 4,
    position: 'relative',
    backgroundColor: 'lightgrey',
    borderRadius: 2
  },
  innerProgressBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: 4,
    backgroundColor: 'lime',
    borderRadius: 2
  },
  iconContainer: {
    display: 'flex',
    marginLeft: 'auto',
  },
  playIcon: {
    position: 'absolute',
    top: 'calc(50% - 2vmin',
    left: 'calc(50% - 2vmin)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1vmin',
    border: '1px solid white',
    borderRadius: '50%',
  },
  bottomBar: {
    position: 'absolute',
    bottom: 0,
    width: '99%',
    maxWidth: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'rgba(0,0,0,0.4)',
    borderBottomRightRadius: 5,
    borderBottomLeftRadius: 5,
    fontSize: '1vmin',
  },
  timestampStyle: {
    padding: '2px 5px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: 180
  },
  durationStyle: {
    padding: '2px 5px',
    borderTopLeftRadius: 5,
    borderBottomRightRadius: 8,
    backgroundColor: 'lightgreen',
    color: 'black',
    marginRight: -1.5,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1vmin',
    marginTop: '2vmin',
  },
  checkbox: {
    width: '1vw',
    height: '1vw',
  },
  downloading: {
    height: '1.5vw',
    width: '1.5vw',
    cursor: 'default',
  },
  download: {
    fontSize: '1vw',
    cursor: 'pointer',
  },
}